import type { JSX } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ChevronDown } from 'lucide-preact';
import './scrubNumber.css';

const GAP_PX = 6;
const VIEW_PAD_PX = 8;

function clamp(value: number, min?: number, max?: number): number {
  let next = value;

  if (min !== undefined) {
    next = Math.max(min, next);
  }

  if (max !== undefined) {
    next = Math.min(max, next);
  }

  return next;
}

/** Decimal places implied by `step` (`0.05` → 2). Integer steps stay whole. */
export function scrubDecimals(step: number): number {
  if (step >= 1) {
    return 0;
  }

  const text = step.toString();
  const exp = /e-(\d+)$/i.exec(text);

  if (exp) {
    return Number(exp[1]);
  }

  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

export function snapScrubValue(value: number, step: number, min?: number, max?: number): number {
  const snapped = step <= 0 ? value : Math.round(value / step) * step;
  return Number(clamp(snapped, min, max).toFixed(scrubDecimals(step > 0 ? step : 1)));
}

export function formatScrubValue(value: number, step: number): string {
  return String(Number(value.toFixed(scrubDecimals(step > 0 ? step : 1))));
}

/** Shift / Ctrl / Cmd: +10 on integer fields, ten steps on fractional ones. */
export function scrubStepDelta(step: number, coarse: boolean): number {
  if (!coarse) {
    return step;
  }

  return step >= 1 ? 10 : snapScrubValue(step * 10, step);
}

export type ScrubNumberProps = {
  label: string;
  value?: number;
  mixed?: boolean;
  min?: number;
  max?: number;
  step?: number;
  /** Open the scrub bar when the number field is focused. The chevron still toggles it. */
  openOnFocus?: boolean;
  /** Min and max labels on either side of the scrub bar. */
  showEnds?: boolean;
  /** Empty blur clears the field instead of restoring the last number. */
  allowEmpty?: boolean;
  onClear?: () => void;
  onChange: (next: number) => void;
};

/**
 * Photoshop-style number field: typeable value, arrows to step, Shift/Ctrl for a coarse jump,
 * and a down-chevron that opens a horizontal scrub bar portaled to `#ui-root`.
 */
export function ScrubNumber({
  label,
  value,
  mixed = false,
  min,
  max,
  step = 1,
  openOnFocus = false,
  showEnds = false,
  allowEmpty = false,
  onClear,
  onChange,
}: ScrubNumberProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null);

  const scrubDisabled = mixed || (value === undefined && !allowEmpty);
  const current = value ?? min ?? 0;

  useEffect(() => {
    const element = inputRef.current;

    if (!element || document.activeElement === element) {
      return;
    }

    if (mixed) {
      element.value = '';
      setSliderOpen(false);
      return;
    }

    if (value !== undefined) {
      element.value = formatScrubValue(value, step);
    } else if (allowEmpty) {
      element.value = '';
    }
  }, [allowEmpty, value, mixed, step]);

  const emit = (next: number): void => {
    const clamped = snapScrubValue(next, step, min, max);

    if (inputRef.current) {
      inputRef.current.value = formatScrubValue(clamped, step);
    }

    onChange(clamped);
  };

  const restore = (): void => {
    if (inputRef.current && !mixed && value !== undefined) {
      inputRef.current.value = formatScrubValue(value, step);
    }
  };

  const commitInput = (raw: string): void => {
    const next = Number(raw);

    if (raw.trim() === '' && allowEmpty) {
      if (inputRef.current) {
        inputRef.current.value = '';
      }

      onClear?.();
      return;
    }

    if (raw.trim() === '' || !Number.isFinite(next)) {
      restore();
      return;
    }

    emit(next);
  };

  const stepBy = (direction: -1 | 1, coarse: boolean): void => {
    if (scrubDisabled) {
      return;
    }

    const typed = Number(inputRef.current?.value);
    const base = Number.isFinite(typed) ? typed : current;
    emit(base + direction * scrubStepDelta(step, coarse));
  };

  const layoutPopover = (): void => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    const rect = root.getBoundingClientRect();
    const width = Math.max(showEnds ? 220 : 140, rect.width);
    const left = Math.min(
      Math.max(VIEW_PAD_PX, rect.left),
      window.innerWidth - width - VIEW_PAD_PX,
    );
    const top = rect.bottom + GAP_PX;

    setBox({ left, top, width });
  };

  useEffect(() => {
    if (!sliderOpen) {
      setBox(null);
      return;
    }

    layoutPopover();

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      const root = rootRef.current;
      const popover = popoverRef.current;

      if (root?.contains(target) || popover?.contains(target)) {
        return;
      }

      setSliderOpen(false);
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setSliderOpen(false);
      }
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', layoutPopover);
    window.addEventListener('scroll', layoutPopover, true);

    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', layoutPopover);
      window.removeEventListener('scroll', layoutPopover, true);
    };
  }, [sliderOpen]);

  const root = typeof document === 'undefined' ? null : document.getElementById('ui-root') ?? document.body;

  const sliderPopover =
    sliderOpen && !scrubDisabled && root && box ? (
      createPortal(
        <div
          ref={popoverRef}
          class="ui-scrub-slider-popover ui-panel ui-chrome"
          role="dialog"
          aria-label={`${label} slider`}
          style={{ left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px` }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div class={showEnds ? 'ui-scrub-slider-ends' : undefined}>
            {showEnds && min !== undefined ? (
              <span class="ui-slider-end">{formatScrubValue(min, step)}</span>
            ) : null}
            <input
              type="range"
              class="ui-scrub-slider-input"
              aria-label={label}
              aria-valuemin={min}
              aria-valuemax={max}
              min={min}
              max={max}
              step={step}
              value={current}
              onInput={(event) => {
                const next = Number(event.currentTarget.value);

                if (inputRef.current && Number.isFinite(next)) {
                  inputRef.current.value = formatScrubValue(next, step);
                }
              }}
              onChange={(event) => emit(Number(event.currentTarget.value))}
            />
            {showEnds && max !== undefined ? (
              <span class="ui-slider-end">{formatScrubValue(max, step)}</span>
            ) : null}
          </div>
        </div>,
        root,
      )
    ) : null;

  const field = (
    <div ref={rootRef} class="ui-scrub-number">
      <span class="ui-scrub-label">{label}</span>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        class="ui-scrub-value"
        role="spinbutton"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        placeholder={mixed ? '—' : undefined}
        onBlur={(event) => {
          commitInput(event.currentTarget.value);

          if (!openOnFocus) {
            return;
          }

          const next = event.relatedTarget;

          if (
            next instanceof Node &&
            (rootRef.current?.contains(next) || popoverRef.current?.contains(next))
          ) {
            return;
          }

          if (next == null) {
            return;
          }

          setSliderOpen(false);
        }}
        onFocus={() => {
          if (openOnFocus) {
            setSliderOpen(true);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            restore();
            event.currentTarget.blur();
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
            event.preventDefault();
            event.stopPropagation();
            stepBy(1, event.shiftKey || event.ctrlKey || event.metaKey);
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
            event.preventDefault();
            event.stopPropagation();
            stepBy(-1, event.shiftKey || event.ctrlKey || event.metaKey);
          }
        }}
      />
      <button
        type="button"
        class="ui-scrub-slider-toggle"
        aria-label={`${label} slider`}
        aria-expanded={sliderOpen ? 'true' : 'false'}
        disabled={scrubDisabled}
        onClick={() => setSliderOpen(!sliderOpen)}
      >
        <ChevronDown size={12} strokeWidth={2.25} aria-hidden="true" />
      </button>
      {sliderPopover}
    </div>
  );

  return field;
}
